<template>
  <section>
    <h1 class="title">{{$t('registrations', 'Registrations')}}</h1>
    <loader :loading="isLoadingRegistrations" :message="$t('loading-registrations', 'Loading registrations...')">
      <data-table
        :data="registrations"
        :columns="[
          {
            key: 'registrationKey',
            displayName: $t('reg-key', 'Reg. key'),
            sortable: false
          },
          {
            key: 'isEnabled',
            displayName: $t('enabled', 'Enabled'),
            sortable: true,
            editable: true,
            searchable: false
          },
          {
            key: 'isValidation',
            displayName: $t('validation', 'Validation'),
            sortable: true,
            editable: true,
            searchable: false
          },
          {
            key: 'firstRegistration',
            displayName: $t('first-reg', 'First reg.'),
            sortable: true,
            type: 'datetime'
          },
          {
            key: 'lastRegistration',
            displayName: $t('last-reg', 'Last reg.'),
            sortable: true,
            type: 'datetime'
          },
          {
            key: 'registrationIndex',
            displayName: $t('reg-idx', 'Idx'),
            sortable: true,
            textAlign: 'right',
            searchable: false
          },
          {
            key: 'isPaid',
            displayName: $t('paid', 'Paid'),
            sortable: true,
            editable: true,
            searchable: false
          },
          {
            key: 'registeredBy',
            displayName: $t('registered-by-short', 'Reg. by'),
            sortable: true
          },
          {
            key: 'isSoldByKG',
            displayName: $t('sold-by-kg', 'Sold by KG'),
            sortable: true,
            editable: true,
            searchable: false
          },
          {
            key: 'company',
            displayName: $t('company', 'Company'),
            sortable: true,
            editable: true
          },
          {
            key: 'contactPerson',
            displayName: $t('contact-person', 'Contact person'),
            sortable: true,
            editable: true
          },
          {
            key: 'email',
            displayName: 'E-mail',
            sortable: true,
            editable: true
          },
          {
            key: 'telephone',
            displayName: $t('telephone', 'Telephone'),
            editable: true,
            sortable: false
          },
          {
            key: 'comment',
            displayName: $t('comment', 'Comment'),
            editable: true
          },
          {
            key: 'isDeleted',
            displayName: $t('deleted', 'Deleted'),
            sortable: true,
            editable: true,
            searchable: false
          }
        ]"
        @onEditRow="editRegistration = $event"
        @onDeleteRow="deleteRegistration"
      >
        <template slot="actions" slot-scope="{ row }">
          <a href="" @click.prevent="renewRegistration(row)" :title="$t('renew', 'Renew')"><font-awesome-icon icon="sync" fixed-width /></a>
        </template>
      </data-table>
    </loader>
    <div v-if="editRegistration" class="modal is-active">
      <div class="modal-background" @click="editRegistration = null"></div>
      <div class="modal-card">
        <div class="modal-card-head">
          <h1 class="title">{{$t('edit-registration', 'Edit registration')}}</h1>
        </div>
        <div class="modal-card-body">
          <registration-form
            ref="editRegistrationForm"
            :default="editRegistration"
            @onSubmitRegistration="updateRegistration"
          />
        </div>
        <div class="modal-card-foot">
          <div class="field is-grouped">
            <button class="button is-primary" @click="$refs.editRegistrationForm.submit()">{{$t('save', 'Save')}}</button>
            <button class="button is-text" @click="editRegistration = null">{{$t('cancel', 'Cancel')}}</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Vue from 'vue'
import DataTable from '@/components/DataTable.vue'
import RegistrationForm from '@/components/RegistrationForm.vue'
import Loader from '@/components/Loader'
import api from '@/api'

export default {
  name: 'Registrations',
  components: { DataTable, RegistrationForm, Loader },
  data () {
    return {
      registrations: [],
      editRegistration: null,
      isLoadingRegistrations: false
    }
  },
  methods: {
    renewRegistration (registration) {
      if (new Date(registration.lastRegistration).getTime() - Date.now() < 365 * 24 * 60 * 60 * 1000) {
        if (!window.confirm(this.$t('warn-renew-reg-less-one-year', 'The registration is less than a year old, renew anyway?'))) {
          return
        }
      } else if (!window.confirm(this.$t('warn-renew-reg', 'Are you sure you want to renew this registration?'))) {
        return
      }

      api.productRegister
        .renew(registration.id)
        .then(() => api.productRegister.get(registration.id))
        .then((res) => {
          const idx = this.registrations.findIndex(reg => reg.id === registration.id)
          Vue.set(this.registrations, idx, res.data)
        })
        .catch((err) => {
          const message = this.$t('error-renewing-registration', {
            replace: { error: err },
            defaultValue: `Error renewing registration: ${err}`
          })
          this.$notifyError(undefined, message)
        })
    },
    updateRegistration (registration) {
      api.productRegister
        .update(registration.id, registration)
        .then((res) => {
          const idx = this.registrations.findIndex(reg => reg.id === registration.id)
          Vue.set(this.registrations, idx, registration)
          this.editRegistration = null
        })
        .catch((err) => {
          const message = this.$t('error-updating-registration', {
            replace: { error: err },
            defaultValue: `Error updating registration: ${err}`
          })
          this.$notifyError(undefined, message)
        })
    },
    deleteRegistration (registration) {
      api.productRegister
        .delete(registration.id)
        .then((res) => {
          const idx = this.registrations.findIndex(reg => reg.id === registration.id)
          Vue.delete(this.registrations, idx)
        })
        .catch((err) => {
          const message = this.$t('error-deleting-registration', {
            replace: { error: err },
            defaultValue: `Error deleting registration: ${err}`
          })

          this.$notifyError(undefined, message)
        })
    }
  },
  mounted () {
    this.isLoadingRegistrations = true

    api.productRegister
      .getAll()
      .then((res) => {
        this.registrations = Array.from(res.data).map(row => Object.freeze(row))
      })
      .catch((err) => {
        const message = this.$t('error-getting-registrations', {
          replace: { error: err },
          defaultValue: `Error getting registration: ${err}`
        })

        this.$notifyError(undefined, message)
      })
      .finally(() => {
        this.isLoadingRegistrations = false
      })
  }
}
</script>
